import { motion } from "framer-motion";
import { FaArrowRight, FaDownload, FaGithub, FaLinkedin } from "react-icons/fa";
import { personalInfo, stats } from "../lib/ui";

export default function Hero() {
  return (
    <section className="relative min-h-[100dvh] pt-28 pb-16 flex items-center">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
          
          {/* ── Main Intro Block ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="md:col-span-12 lg:col-span-8 glass-card rounded-[32px] p-8 sm:p-10 lg:p-12 relative overflow-hidden hover-glow flex flex-col justify-center"
          >
            {/* Background glowing blob */}
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-app-accent/10 rounded-full blur-[120px] pointer-events-none" />
            
            {/* Availability badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-app-accent/10 text-app-accent border border-app-accent/20 text-sm font-medium mb-8 w-max shadow-sm">
              <span className="w-2 h-2 rounded-full bg-app-accent animate-pulse" />
              {personalInfo.availability}
            </div>

            <h1
              className="font-display font-bold leading-[1.1] text-app-text mb-6 relative z-10"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
            >
              Hi, I'm{" "}
              <span className="text-gradient-cyan">
                {personalInfo.firstName}
              </span>
              <br />
              <span className="text-app-text">{personalInfo.lastName}</span>
            </h1>

            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-app-accent text-sm">&lt;</span>
              <h2 className="font-display text-xl md:text-2xl font-semibold text-app-mute">
                {personalInfo.role}
              </h2>
              <span className="font-mono text-app-accent text-sm">/&gt;</span>
            </div>

            <p className="text-app-mute text-base md:text-lg max-w-xl leading-relaxed mb-10">
              {personalInfo.bio}
            </p>

            <div className="flex flex-wrap items-center gap-4 relative z-10">
              <a
                href="#services"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-sm font-semibold text-white transition-all duration-300 shadow-glow hover:shadow-glow-lg hover:-translate-y-1"
                style={{
                  background: "linear-gradient(135deg, #3fa9f5, #1a8ee0)",
                }}
              >
                View My Skills
                <FaArrowRight
                  size={13}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-sm font-semibold text-app-text bg-app-panel/80 backdrop-blur-md border border-app-border hover:border-app-accent hover:bg-app-accent/10 transition-all duration-300 hover:-translate-y-1"
              >
                <FaDownload size={13} />
                Contact Me
              </a>
            </div>
          </motion.div>

          {/* ── Profile Image Block ── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-6 lg:col-span-4 glass-card rounded-[32px] p-6 relative flex flex-col items-center justify-center overflow-hidden hover-glow min-h-[300px]"
          >
            {/* Spinning background effect - disabled on mobile for performance */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute w-[150%] h-[150%] bg-[conic-gradient(from_0deg,transparent_0_340deg,rgba(16,185,129,0.05)_360deg)] hidden md:block"
            />
            
            <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-full shadow-glow bg-app-panel p-2 mb-6 z-10">
              <img
                src={personalInfo.avatar}
                alt={`Professional portrait of ${personalInfo.fullName} - ${personalInfo.role}`}
                className="w-full h-full rounded-full object-cover border-4 border-app-soft"
                loading="eager"
              />
              {/* Floating tech badge */}
              <div className="absolute -bottom-2 -right-2 bg-app-panel rounded-xl px-3 py-2 shadow-sm border border-app-border flex items-center gap-2">
                 <span className="w-2 h-2 rounded-full bg-app-secondary" />
                 <span className="font-mono text-[10px] font-bold text-app-mute">Consultant</span>
              </div>
            </div>

            <div className="flex gap-3 z-10">
              <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-app-soft border border-app-border text-app-mute hover:text-app-accent hover:border-app-accent flex items-center justify-center transition-all" aria-label={`View ${personalInfo.fullName}'s GitHub Profile`}>
                <FaGithub size={18} />
              </a>
              <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-xl bg-app-soft border border-app-border text-app-mute hover:text-app-secondary hover:border-app-secondary flex items-center justify-center transition-all" aria-label={`View ${personalInfo.fullName}'s LinkedIn Profile`}>
                <FaLinkedin size={18} />
              </a>
            </div>
          </motion.div>

          {/* ── Stat Blocks ── */}
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
              className="md:col-span-4 lg:col-span-4 glass-card rounded-3xl p-6 sm:p-8 flex flex-col items-center justify-center text-center hover-glow group"
            >
              <h3 className="font-display text-4xl lg:text-5xl font-bold text-gradient-cyan mb-2 group-hover:scale-105 transition-transform duration-300">
                {stat.value}
              </h3>
              <p className="text-sm font-medium text-app-mute uppercase tracking-widest">
                {stat.label}
              </p>
            </motion.div>
          ))}
          
        </div>
      </div>
    </section>
  );
}
